import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import { OBComments, OBCommentsReq, ResetManeuverItem } from "src/app/models/models";
import { obEventMatchesNaScope, obEventMatchesRole, parseObLabel, parseObsCode } from "../../shared-v2/utils/observation.utils";
import { isTs1ManeuverOutcomeNaSummary } from "../utils/ts1-rating-outcome-interaction";
import { formatTs1SummaryCompetencyStatusLabel } from "../utils/ts1-summary-display.utils";
import { TS1_REVIEW_COMPETENCY_ORDER } from "../utils/ts1-rating-review.utils";

export const MANEUVER_RATINGS_OB_LABEL = "Maneuver Ratings";
export const TEM_COMPETENCY_PREVIEW_GROUP_LABEL = "Rating per Competency";

export interface Ts1PreviewCompetencyCommentGroup {
	code: string;
	/** Full heading text (code + description). */
	label: string;
	/** Competency description without code prefix. */
	description?: string;
	comments: OBComments[];
}

export interface Ts1PreviewManeuverCommentGroup {
	maneuverCode: string;
	label: string;
	comments: OBComments[];
}

export interface Ts1PreviewCompetencyCommentOptions {
	candidateId?: string;
	candidateRole?: CandidateRole;
	addRemoveOb?: OBCommentsReq[];
	obList?: ObListEntry[];
	preserveCommentOrder?: boolean;
}

type ObListEntry = { title?: string; desc?: string };

/** Competency OB rows only (e.g. KNO 1, PRO 5) — not maneuver outcomes, TEM, or Rating C. */
export function isCompetencyObComment(comment: OBComments): boolean {
	if (comment.isManeuverOutcome || comment.isTemCompetencyOutcome) return false;
	if (comment.ob === "REPEAT" || comment.ob === "TEM") return false;

	if (comment.observationCode) {
		const { cat, num } = parseObsCode(comment.observationCode);
		if (num != null && TS1_REVIEW_COMPETENCY_ORDER.includes(cat)) {
			return true;
		}
	}

	const { cat, num } = parseObLabel(comment.ob);
	return num != null && TS1_REVIEW_COMPETENCY_ORDER.includes(cat);
}

function competencyCodeFromComment(comment: OBComments): string | null {
	if (!isCompetencyObComment(comment)) return null;

	if (comment.observationCode) {
		const { cat, num } = parseObsCode(comment.observationCode);
		if (num != null && TS1_REVIEW_COMPETENCY_ORDER.includes(cat)) {
			return cat;
		}
	}

	const { cat, num } = parseObLabel(comment.ob);
	return num != null && TS1_REVIEW_COMPETENCY_ORDER.includes(cat) ? cat : null;
}

/** Maneuver-linked rows for Preview Summary — OB, maneuver outcomes, REPEAT; not TEM Rating C. */
export function isManeuverLinkedPreviewComment(comment: OBComments): boolean {
	if (comment.isTemCompetencyOutcome) return false;
	if (!comment.maneuverCode && !comment.maneuver?.trim()) return false;
	return true;
}

function maneuverKeyFromComment(comment: OBComments): string | null {
	if (!isManeuverLinkedPreviewComment(comment)) return null;
	if (comment.maneuverCode?.trim()) return comment.maneuverCode.trim();
	const label = comment.maneuver?.replace(/^R\d+\s+/i, "").trim();
	return label || null;
}

function commentTimestamp(comment: OBComments): number {
	if (!comment.createdAt) return Number.NaN;
	const timestamp = new Date(comment.createdAt).getTime();
	return Number.isNaN(timestamp) ? Number.NaN : timestamp;
}

function commentTimeMs(comment: OBComments): number {
	const timestamp = commentTimestamp(comment);
	return Number.isNaN(timestamp) ? 0 : timestamp;
}

/** Attempt index from repeatCount or R# maneuver prefix (0 = pre-first-repeat). */
function commentAttempt(comment: OBComments): number {
	if (
		typeof comment.repeatCount === "number" &&
		Number.isFinite(comment.repeatCount) &&
		comment.repeatCount >= 0
	) {
		return comment.repeatCount;
	}
	const match = comment.maneuver?.match(/^R(\d+)\s+/i);
	if (match) return Number(match[1]);
	return 0;
}

function sortCommentsByTime(a: OBComments, b: OBComments): number {
	return commentTimeMs(a) - commentTimeMs(b);
}

/** Within an attempt: chronological (OBs and Maneuver Ratings mixed by createdAt). */
function sortCommentsObsThenOutcomes(a: OBComments, b: OBComments): number {
	return sortCommentsByTime(a, b);
}

function dedupeRepeatRows(repeatRows: OBComments[]): OBComments[] {
	const byAttempt = new Map<number, OBComments>();
	for (const row of repeatRows) {
		const attempt = Math.max(1, commentAttempt(row) || 1);
		const existing = byAttempt.get(attempt);
		if (!existing || commentTimeMs(row) >= commentTimeMs(existing)) {
			byAttempt.set(attempt, { ...row, repeatCount: attempt });
		}
	}
	return [...byAttempt.entries()]
		.sort((a, b) => a[0] - b[0] || commentTimeMs(a[1]) - commentTimeMs(b[1]))
		.map(([, row]) => row);
}

/**
 * Untagged path: split OBs around each REPEAT by createdAt
 * (pre → REPEAT → between → REPEAT → post). Equal timestamps place OB before REPEAT.
 */
function orderUntaggedByRepeatTimestamps(
	others: OBComments[],
	repeatRows: OBComments[],
): OBComments[] {
	const result: OBComments[] = [];
	const remaining = [...others].sort(sortCommentsObsThenOutcomes);
	let fromMs = Number.NEGATIVE_INFINITY;

	for (const repeat of repeatRows) {
		const repeatMs = commentTimeMs(repeat);
		const pre: OBComments[] = [];
		const rest: OBComments[] = [];
		for (const row of remaining) {
			const time = commentTimeMs(row);
			if (time > fromMs && time <= repeatMs) {
				pre.push(row);
			} else {
				rest.push(row);
			}
		}
		result.push(
			...pre.map((comment) => ({ ...comment, isRepeat: true })),
			{ ...repeat, isRepeat: false },
		);
		remaining.length = 0;
		remaining.push(...rest);
		fromMs = repeatMs;
	}

	result.push(...remaining.map((comment) => ({ ...comment, isRepeat: false })));
	return result;
}

/**
 * TS-1 v3 only — order one maneuver's comments as: pre-repeat → REPEAT → post-repeat (per attempt).
 * Mirrors backend orderManeuverCommentsByRepeatTimeline / applyRepeatTimelineToSummaryComments.
 */
function sortManeuverCommentsHistorically(comments: OBComments[]): OBComments[] {
	const others = comments.filter((comment) => comment.ob !== "REPEAT");
	const repeatRows = dedupeRepeatRows(comments.filter((comment) => comment.ob === "REPEAT"));

	if (!repeatRows.length) {
		return [...comments].sort(sortCommentsObsThenOutcomes);
	}

	const hasAttemptTaggedPost = others.some((comment) => commentAttempt(comment) > 0);

	if (!hasAttemptTaggedPost) {
		return orderUntaggedByRepeatTimestamps(others, repeatRows);
	}

	const result: OBComments[] = [];
	let minAttempt = 0;
	for (const repeat of repeatRows) {
		const attempt = Math.max(1, commentAttempt(repeat) || 1);
		const preBatch = others
			.filter(
				(comment) => commentAttempt(comment) >= minAttempt && commentAttempt(comment) < attempt,
			)
			.sort(sortCommentsObsThenOutcomes)
			.map((comment) => ({ ...comment, isRepeat: true }));
		result.push(...preBatch);
		result.push({ ...repeat, isRepeat: false });
		minAttempt = attempt;
	}

	const postBatch = others
		.filter((comment) => commentAttempt(comment) >= minAttempt)
		.sort(sortCommentsObsThenOutcomes)
		.map((comment) => ({ ...comment, isRepeat: false }));
	result.push(...postBatch);
	return result;
}

function belongsToCandidate(
	comment: OBComments,
	candidateId?: string,
	candidateRole?: CandidateRole,
): boolean {
	// Maneuver-wide REPEAT — shared across both seats; groups still key by maneuverCode.
	if (comment.ob === "REPEAT") {
		return true;
	}

	if (candidateId && comment.candidateId && comment.candidateId !== candidateId) {
		return false;
	}

	// Per-candidate preview (Step 3 / form summary) includes every PF/PM slot row for that
	// candidate; role is shown on each row instead of filtering by assigned crew role.
	if (candidateId && comment.candidateId === candidateId) {
		return true;
	}

	return obEventMatchesRole(comment, candidateRole);
}

type ObservationCodeParts = {
	observationCode?: string;
	ob?: string;
};

function observationCodeForSummaryKey(comment: ObservationCodeParts): string {
	if (comment.observationCode) return comment.observationCode;
	if (!comment.ob) return "";
	const { cat, num } = parseObLabel(comment.ob);
	if (!cat || num == null) return comment.ob.toLowerCase();
	return `${cat.toLowerCase()}_${num}`;
}

type NaObCellRef = Pick<OBComments, "candidateId" | "maneuverCode" | "observationCode" | "candidateRole"> & {
	ob?: string;
};

function summaryObEventKey(parts: {
	candidateId?: string;
	maneuverCode?: string;
	observationCode?: string;
	candidateRole?: CandidateRole | string | null;
}): string {
	return `${parts.candidateId ?? ""}::${parts.maneuverCode ?? ""}::${parts.observationCode ?? ""}::${parts.candidateRole ?? ""}`;
}

function commentSummaryObKey(comment: OBComments): string {
	return summaryObEventKey({
		candidateId: comment.candidateId,
		maneuverCode: comment.maneuverCode,
		observationCode: observationCodeForSummaryKey(comment),
		candidateRole: comment.candidateRole,
	});
}

function normalizedObservationCode(parts: ObservationCodeParts): string {
	return observationCodeForSummaryKey(parts).toLowerCase();
}

/** Latest N/A toggle for one OB cell — strict PF/PM scope (TS1-v3). */
export function resolveObCurrentlyMarkedNa(comment: NaObCellRef, addRemoveOb: OBCommentsReq[] = []): boolean {
	const observationCode = normalizedObservationCode(comment);
	let latest: { at: number; order: number; disabled: boolean } | null = null;
	let order = 0;

	for (const event of addRemoveOb) {
		if (event.isReset || event.isRepeat) {
			order++;
			continue;
		}

		const isNaToggle =
			event.description === CONSTANT.NOT_APPLICABLE_NOTES ||
			event.description === CONSTANT.RESET_NOT_APPLICABLE_NOTES ||
			event.description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
			event.description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES;
		if (!isNaToggle) {
			order++;
			continue;
		}

		if (event.candidateId !== comment.candidateId || event.maneuverCode !== comment.maneuverCode) {
			order++;
			continue;
		}

		if (normalizedObservationCode(event) !== observationCode) {
			order++;
			continue;
		}

		if (!obEventMatchesNaScope(event, comment.candidateRole)) {
			order++;
			continue;
		}

		const at = event.createdAt ? new Date(event.createdAt).getTime() : order;
		const disabled =
			event.description === CONSTANT.NOT_APPLICABLE_NOTES ||
			event.description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES;
		if (!latest || at > latest.at || (at === latest.at && order > latest.order)) {
			latest = { at, order, disabled };
		}
		order++;
	}

	return latest?.disabled ?? false;
}

/** Latest N/A state per OB — keyed like active summary rows (candidate + maneuver + obs + role). */
export function buildLatestObNaDisabledByKey(addRemoveOb: OBCommentsReq[] = []): Map<string, boolean> {
	const probeKeys = new Map<string, NaObCellRef>();

	for (const event of addRemoveOb) {
		if (event.isReset || event.isRepeat) continue;
		const isNaToggle =
			event.description === CONSTANT.NOT_APPLICABLE_NOTES ||
			event.description === CONSTANT.RESET_NOT_APPLICABLE_NOTES ||
			event.description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
			event.description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES;
		if (!isNaToggle) continue;

		const key = summaryObEventKey({
			candidateId: event.candidateId,
			maneuverCode: event.maneuverCode,
			observationCode: event.observationCode,
			candidateRole: event.candidateRole,
		});
		if (!probeKeys.has(key)) {
			probeKeys.set(key, {
				candidateId: event.candidateId,
				maneuverCode: event.maneuverCode,
				observationCode: event.observationCode,
				candidateRole: event.candidateRole,
				ob: event.observationCode ?? "",
			});
		}
	}

	const result = new Map<string, boolean>();
	for (const [key, probe] of probeKeys) {
		if (resolveObCurrentlyMarkedNa(probe, addRemoveOb)) {
			result.set(key, true);
		}
	}
	return result;
}

export function isTs1InstructorCommentVisible(
	comment: OBComments,
	_naDisabledByKey?: Map<string, boolean>,
	_addRemoveOb?: OBCommentsReq[],
): boolean {
	if (comment.isReset || comment.lastActionDisabled) {
		return false;
	}

	// Remarks already show "N/A" — hide redundant fixed "Maneuver Outcome N/A" text.
	if (isTs1ManeuverOutcomeNaSummary(comment)) {
		return false;
	}

	if (comment.isDisabled) {
		return false;
	}

	return !!comment.instructorComment?.trim();
}

/** Preview maneuver groups — OB add/remove without text; instructor text required for outcomes. */
export function isTs1PreviewManeuverCommentVisible(
	comment: OBComments,
	addRemoveOb?: OBCommentsReq[],
): boolean {
	if (!isManeuverLinkedPreviewComment(comment)) return false;
	if (comment.isTemCompetencyOutcome) return false;
	return isTs1Step2SummaryRowVisible(comment, addRemoveOb ?? []);
}

export function filterTs1InstructorComments(comments: OBComments[], addRemoveOb?: OBCommentsReq[]): OBComments[] {
	return (comments ?? []).filter((comment) => isTs1InstructorCommentVisible(comment, undefined, addRemoveOb));
}

function isUndoneNaCommentRow(comment: OBComments): boolean {
	if (comment.isManeuverOutcome || comment.isTemCompetencyOutcome) return false;
	if (comment.lastActionDisabled) return true;
	const text = (comment.instructorComment ?? "").trim().toUpperCase();
	return text === CONSTANT.RESET_NOT_APPLICABLE_NOTES;
}

/**
 * Collapse same-attempt [N/A, Undo N/A] pairs in summary comments without crossing a REPEAT boundary.
 */
export function collapseTs1UndoneNaComments(comments: OBComments[]): OBComments[] {
	const result: OBComments[] = [];
	for (const row of comments ?? []) {
		if (!isUndoneNaCommentRow(row)) {
			result.push(row);
			continue;
		}

		const rowKey = maneuverKeyFromComment(row);
		const rowObs = normalizedObservationCode(row);
		const rowAttempt = commentAttempt(row);

		for (let i = result.length - 1; i >= 0; i--) {
			const prev = result[i];
			if (row.candidateId && prev.candidateId && prev.candidateId !== row.candidateId) continue;
			if (rowKey && maneuverKeyFromComment(prev) !== rowKey) continue;
			if (prev.ob === "REPEAT") break;
			if (commentAttempt(prev) !== rowAttempt || Boolean(prev.isRepeat) !== Boolean(row.isRepeat)) {
				break;
			}
			if (
				prev.isObservationComment ||
				prev.isManeuverOutcome ||
				prev.isTemCompetencyOutcome ||
				!prev.isDisabled
			) {
				continue;
			}
			if (normalizedObservationCode(prev) !== rowObs) continue;
			if (!obEventMatchesNaScope(prev, row.candidateRole)) continue;
			result.splice(i, 1);
			break;
		}
	}
	return result;
}

/** Step 2 inline summary — OB add/remove rows (+ REPEAT dividers); instructor text for outcomes. N/A status lives on the OB panel. */
export function isTs1Step2SummaryRowVisible(
	comment: OBComments,
	addRemoveOb: OBCommentsReq[] = [],
): boolean {
	if (comment.ob === "REPEAT") {
		return true;
	}

	if (comment.isReset || comment.lastActionDisabled || isUndoneNaCommentRow(comment)) {
		return false;
	}

	if (
		comment.isDisabled &&
		!comment.isManeuverOutcome &&
		!comment.isTemCompetencyOutcome
	) {
		return true;
	}

	if (
		!comment.isManeuverOutcome &&
		!comment.isTemCompetencyOutcome &&
		!comment.isObservationComment &&
		comment.action != null &&
		comment.ob !== "TEM"
	) {
		return true;
	}

	return isTs1InstructorCommentVisible(comment, undefined, addRemoveOb);
}

export function filterTs1Step2SummaryComments(comments: OBComments[], addRemoveOb?: OBCommentsReq[]): OBComments[] {
	return collapseTs1UndoneNaComments(comments ?? []).filter((comment) =>
		isTs1Step2SummaryRowVisible(comment, addRemoveOb ?? []),
	);
}

export function buildTs1PreviewCompetencyCommentGroups(
	comments: OBComments[],
	obList: ObListEntry[],
	options: Ts1PreviewCompetencyCommentOptions = {},
): Ts1PreviewCompetencyCommentGroup[] {
	const { candidateId, candidateRole, addRemoveOb } = options;
	const descByCode = new Map(
		obList.map((entry) => [(entry.title ?? "").toUpperCase(), (entry.desc ?? "").trim()]),
	);
	const byCode = new Map<string, OBComments[]>();

	for (const comment of comments) {
		if (!belongsToCandidate(comment, candidateId, candidateRole)) continue;
		if (!isTs1InstructorCommentVisible(comment, undefined, addRemoveOb)) continue;

		const code = competencyCodeFromComment(comment);
		if (!code) continue;

		const bucket = byCode.get(code) ?? [];
		bucket.push(comment);
		byCode.set(code, bucket);
	}

	return TS1_REVIEW_COMPETENCY_ORDER.filter((code) => byCode.has(code)).map((code) => {
		const description = descByCode.get(code);
		return {
			code,
			description,
			label: description ? `${code} · ${description}` : code,
			comments: byCode.get(code) ?? [],
		};
	});
}

export function buildTs1PreviewManeuverCommentGroups(
	comments: OBComments[],
	maneuverRows: Array<{ maneuverCode: string; label: string }>,
	options: Ts1PreviewCompetencyCommentOptions = {},
): Ts1PreviewManeuverCommentGroup[] {
	const { candidateId, candidateRole, addRemoveOb, preserveCommentOrder } = options;
	const byKey = new Map<string, OBComments[]>();

	for (const comment of collapseTs1UndoneNaComments(comments ?? [])) {
		if (!belongsToCandidate(comment, candidateId, candidateRole)) continue;
		if (!isTs1PreviewManeuverCommentVisible(comment, addRemoveOb)) continue;

		const key = maneuverKeyFromComment(comment);
		if (!key) continue;

		const bucket = byKey.get(key) ?? [];
		bucket.push(comment);
		byKey.set(key, bucket);
	}

	const orderBucket = (bucket: OBComments[]) =>
		preserveCommentOrder ? bucket : sortManeuverCommentsHistorically(bucket);

	const labelByCode = new Map(maneuverRows.map((row) => [row.maneuverCode, row.label]));
	const orderedCodes = new Set(maneuverRows.map((row) => row.maneuverCode));
	const groups: Ts1PreviewManeuverCommentGroup[] = [];

	for (const row of maneuverRows) {
		const bucket = byKey.get(row.maneuverCode);
		if (!bucket?.length) continue;

		groups.push({
			maneuverCode: row.maneuverCode,
			label: row.label,
			comments: orderBucket(bucket),
		});
	}

	const orphanKeys = [...byKey.keys()].filter((key) => !orderedCodes.has(key));
	orphanKeys.sort((a, b) => {
		const labelA = (byKey.get(a)?.[0]?.maneuver ?? a).toLowerCase();
		const labelB = (byKey.get(b)?.[0]?.maneuver ?? b).toLowerCase();
		return labelA.localeCompare(labelB);
	});

	for (const key of orphanKeys) {
		const bucket = byKey.get(key);
		if (!bucket?.length) continue;

		groups.push({
			maneuverCode: key,
			label: labelByCode.get(key) ?? bucket[0]?.maneuver ?? key,
			comments: orderBucket(bucket),
		});
	}

	return groups;
}

/** TEM Safety Outcome rows (PRO - C, KNO - C, …) for Preview Summary comments. */
export function buildTs1PreviewTemCompetencyCommentGroups(
	comments: OBComments[],
	options: Ts1PreviewCompetencyCommentOptions = {},
): Ts1PreviewManeuverCommentGroup[] {
	const { candidateId, candidateRole, addRemoveOb } = options;
	const bucket: OBComments[] = [];

	for (const comment of comments) {
		if (!comment.isTemCompetencyOutcome) continue;
		if (!belongsToCandidate(comment, candidateId, candidateRole)) continue;
		if (!isTs1InstructorCommentVisible(comment, undefined, addRemoveOb)) continue;
		bucket.push(comment);
	}

	if (!bucket.length) return [];

	bucket.sort((a, b) => {
		const codeA = a.competencyCode ?? "";
		const codeB = b.competencyCode ?? "";
		const orderA = TS1_REVIEW_COMPETENCY_ORDER.indexOf(codeA);
		const orderB = TS1_REVIEW_COMPETENCY_ORDER.indexOf(codeB);
		if (orderA !== orderB) return orderA - orderB;

		const ta = commentTimestamp(a);
		const tb = commentTimestamp(b);
		if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return ta - tb;
		return 0;
	});

	return [
		{
			maneuverCode: TEM_COMPETENCY_PREVIEW_GROUP_LABEL,
			label: TEM_COMPETENCY_PREVIEW_GROUP_LABEL,
			comments: bucket,
		},
	];
}

export function buildTs1PreviewCommentGroups(
	comments: OBComments[],
	maneuverRows: Array<{ maneuverCode: string; label: string }>,
	options: Ts1PreviewCompetencyCommentOptions = {},
): Ts1PreviewManeuverCommentGroup[] {
	return [
		...buildTs1PreviewManeuverCommentGroups(comments, maneuverRows, options),
		...buildTs1PreviewTemCompetencyCommentGroups(comments, options),
	];
}

export function previewCommentObTagClass(comment: OBComments): string {
	if (comment.isManeuverOutcome || comment.isTemCompetencyOutcome) return "";
	if (comment.isDisabled || comment.lastActionDisabled) {
		return "ts1-preview-summary__comment-tag--neutral";
	}
	if (comment.action === true) return "ts1-preview-summary__comment-tag--green";
	if (comment.action === false) return "ts1-preview-summary__comment-tag--red";
	return "ts1-preview-summary__comment-tag--neutral";
}

export function previewCommentObLabel(comment: OBComments): string {
	if (comment.isManeuverOutcome) return MANEUVER_RATINGS_OB_LABEL;
	return comment.ob ?? "";
}

export function previewCommentPrimaryTagLabel(
	comment: OBComments,
	resetManeuvers: ResetManeuverItem[] = [],
): string {
	if (comment.isTemCompetencyOutcome) {
		return formatTs1SummaryCompetencyStatusLabel(comment, resetManeuvers);
	}
	return previewCommentObLabel(comment);
}

/** Maneuver / TEM outcome rating label (e.g. Rating 5) shown between role and instructor comment. */
export function previewCommentOutcomeRemarks(comment: OBComments): string {
	if (!comment.isManeuverOutcome && !comment.isTemCompetencyOutcome) return "";
	if (comment.remarks?.trim()) return comment.remarks.trim();
	if (comment.isDisabled || comment.remarks === "N/A") return "N/A";
	if (typeof comment.rate === "number" && comment.rate > 0) {
		return `Rating ${comment.rate}`;
	}
	return "";
}

export function previewCommentOutcomeRateClass(rate?: number | null): string {
	if (rate === 1) return "text-red";
	if (rate === 2) return "text-yellow";
	if (rate === 3) return "text-yellow-60";
	if (rate === 4) return "text-green";
	if (rate === 5) return "text-green-90";
	return "text-grey-60";
}

export function previewCommentRoleLabel(role?: CandidateRole | string | null): string {
	if (role === CandidateRole.PF || role === CONSTANT.CANDIDATE_ROLE.PF) return "PF";
	if (role === CandidateRole.PM || role === CONSTANT.CANDIDATE_ROLE.PM) return "PM";
	return "";
}

/** Plain N/A suffix for View Comments — shown after role label (no braces). */
export function previewCommentNaSuffix(comment: OBComments): string {
	if (comment.isReset || comment.isManeuverOutcome || comment.isTemCompetencyOutcome) {
		return "";
	}
	if (comment.lastActionDisabled) return "Undo N/A";
	if (comment.isDisabled) return "N/A";
	return "";
}
